import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, ShieldCheck,
  Clock, CheckCircle, XCircle, AlertCircle,
  Loader2, ChevronDown, LogOut, Eye, X, ShoppingBag,
  ListOrdered, FileText, UserCircle, Settings as SettingsIcon,
} from 'lucide-react';
import { getAuthToken } from '@/services/memberApi';
import { shopAdminFetch } from '@/services/shopAdminApi';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const API = `${API_BASE_URL}/admin`;

// ============================================================================
// TYPES
// ============================================================================

interface DashboardStats {
  pending_agent_applications: number;
  total_approved_agents: number;
  total_members: number;
  pending_super_admin_approvals: number;
}

interface AgentApplication {
  application_id: string;
  user_id: string;
  email: string;
  first_name: string;
  surname: string;
  phone: string | null;
  region: string | null;
  motivation: string | null;
  sponsor_name: string | null;
  sponsor_code: string | null;
  applied_at: string;
  status: string;
  rejection_reason: string | null;
}

interface Agent {
  user_id: string;
  email: string;
  first_name: string;
  surname: string;
  agent_code: string | null;
  agent_status: string;
  region: string | null;
  sponsor_name: string | null;
  approved_at: string | null;
}

interface SuperAdmin {
  user_id: string;
  email: string;
  first_name: string;
  surname: string;
  is_approved: boolean;
  approved_at: string | null;
  approved_by_email: string | null;
  created_at: string;
}

interface WooOrder {
  id: number;
  status?: string;
  total?: string;
  currency?: string;
  date_created?: string;
  billing?: { first_name?: string; last_name?: string };
}

interface DashboardOverview {
  revenue30d: number;
  orders30d: number;
  newCustomers30d: number;
  recentOrders: WooOrder[];
  lowStockCount: number;
}

interface WooProductStock {
  id: number;
  stock_status?: string;
}

interface WooCustomer {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  orders_count?: number;
  is_paying_customer?: boolean;
}

interface ContentItem {
  id: string;
  title: string;
  type: 'Page' | 'Journal';
  status: 'Published' | 'Draft';
  updated: string;
}

// Placeholder content — no CMS is wired up yet. Static/local-only data.
const PLACEHOLDER_CONTENT: ContentItem[] = [
  { id: 'pg-1', title: 'Home', type: 'Page', status: 'Published', updated: '2026-06-18' },
  { id: 'pg-2', title: 'About Marple & Company', type: 'Page', status: 'Published', updated: '2026-05-02' },
  { id: 'pg-3', title: 'Shipping & Returns', type: 'Page', status: 'Draft', updated: '2026-06-30' },
  { id: 'jr-1', title: 'The ritual of the morning oil', type: 'Journal', status: 'Published', updated: '2026-06-21' },
  { id: 'jr-2', title: 'Sourcing rooibos from the Cederberg', type: 'Journal', status: 'Draft', updated: '2026-06-27' },
];

const ORDER_STATUS_FILTERS = ['All', 'Paid', 'Pending', 'Shipped', 'Refunded'] as const;

// ============================================================================
// API HELPERS
// ============================================================================

async function adminFetch(path: string, options: RequestInit = {}) {
  const token = getAuthToken();
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw Object.assign(new Error(err.detail || 'Request failed'), { status: res.status });
  }
  return res.json();
}

// ============================================================================
// STAT CARD
// ============================================================================

function StatCard({
  icon: Icon, label, value, color,
}: { icon: React.ElementType; label: string; value: number | string; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-semibold text-[#1A2E12]">{value}</p>
        <p className="text-sm text-[#5E6B52]">{label}</p>
      </div>
    </div>
  );
}

// ============================================================================
// REVIEW MODAL
// ============================================================================

function ReviewModal({
  application,
  onClose,
  onDone,
}: {
  application: AgentApplication;
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    if (!action) return;
    if (action === 'reject' && !rejectionReason.trim()) {
      setError('Please provide a rejection reason.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await adminFetch(`/agents/${application.user_id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(
          action === 'approve'
            ? { notes }
            : { rejection_reason: rejectionReason.trim(), notes }
        ),
      });
      onDone();
    } catch (err: any) {
      setError(err?.message || 'Failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[#D9B38C]/20">
          <h2 className="font-semibold text-[#1A2E12]">Review Application</h2>
          <button onClick={onClose} className="text-[#5E6B52] hover:text-[#1A2E12]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[#5E6B52]">Name</p>
              <p className="font-medium text-[#1A2E12]">{application.first_name} {application.surname}</p>
            </div>
            <div>
              <p className="text-[#5E6B52]">Email</p>
              <p className="font-medium text-[#1A2E12]">{application.email}</p>
            </div>
            <div>
              <p className="text-[#5E6B52]">Phone</p>
              <p className="font-medium text-[#1A2E12]">{application.phone || '—'}</p>
            </div>
            <div>
              <p className="text-[#5E6B52]">Province</p>
              <p className="font-medium text-[#1A2E12]">{application.region || '—'}</p>
            </div>
            <div>
              <p className="text-[#5E6B52]">Sponsor</p>
              <p className="font-medium text-[#1A2E12]">{application.sponsor_name || 'Marple Root Agent'}</p>
            </div>
            <div>
              <p className="text-[#5E6B52]">Applied</p>
              <p className="font-medium text-[#1A2E12]">
                {new Date(application.applied_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>

          {application.motivation && (
            <div className="bg-[#F7F5EE] rounded-xl p-3">
              <p className="text-[#5E6B52] text-xs mb-1">Motivation</p>
              <p className="text-[#1A2E12]">{application.motivation}</p>
            </div>
          )}

          <div>
            <label className="block text-[#5E6B52] mb-1">Internal notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#BDD630] resize-none"
              placeholder="Notes visible only to admins…"
            />
          </div>

          {action === 'reject' && (
            <div>
              <label className="block text-[#5E6B52] mb-1">Rejection reason <span className="text-red-500">*</span></label>
              <textarea
                rows={2}
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none"
                placeholder="Reason shown to applicant…"
              />
            </div>
          )}

          {error && (
            <div className="flex gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {action !== 'reject' && (
              <button
                onClick={() => action === 'approve' ? submit() : setAction('approve')}
                disabled={submitting}
                className="flex-1 bg-[#BDD630] text-[#1A2E12] font-semibold py-2.5 rounded-xl hover:bg-[#a8be2a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting && action === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {action === 'approve' ? 'Confirm Approve' : 'Approve'}
              </button>
            )}
            {action !== 'approve' && (
              <button
                onClick={() => action === 'reject' ? submit() : setAction('reject')}
                disabled={submitting}
                className="flex-1 bg-red-50 text-red-600 font-semibold py-2.5 rounded-xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2 border border-red-200 disabled:opacity-50"
              >
                {submitting && action === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                {action === 'reject' ? 'Confirm Reject' : 'Reject'}
              </button>
            )}
            {action && (
              <button onClick={() => { setAction(null); setError(''); }} className="px-4 py-2.5 text-[#5E6B52] hover:text-[#1A2E12] text-sm">
                Back
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN ADMIN PORTAL
// ============================================================================

type Tab =
  | 'dashboard'
  | 'orders'
  | 'pending'
  | 'agents'
  | 'content'
  | 'customers'
  | 'super-admins'
  | 'settings';

export default function AdminPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [authChecked, setAuthChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Data
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [overviewError, setOverviewError] = useState('');
  const [pending, setPending] = useState<AgentApplication[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [agentStatusFilter, setAgentStatusFilter] = useState('');

  // Orders
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState<typeof ORDER_STATUS_FILTERS[number]>('All');
  const [ordersError, setOrdersError] = useState('');

  // Customers
  const [customers, setCustomers] = useState<WooCustomer[]>([]);
  const [customersError, setCustomersError] = useState('');

  // Settings (local only — no backend persistence yet)
  const [settingsStoreName, setSettingsStoreName] = useState('Marple & Company');
  const [settingsSupportEmail, setSettingsSupportEmail] = useState('support@marple.co.za');
  const [settingsMaintenanceMode, setSettingsMaintenanceMode] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<AgentApplication | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  // Auth check
  useEffect(() => {
    adminFetch('/dashboard')
      .then(data => {
        setStats(data);
        setIsSuperAdmin(true);
        setAuthChecked(true);
      })
      .catch(err => {
        if (err?.status === 401 || err?.status === 403) {
          navigate('/member/login');
          return;
        }
        // Fail CLOSED. This previously granted super-admin on any non-401/403
        // error — including a bare network TypeError (which has no `.status`)
        // and any 5xx — so an unauthenticated visitor got the whole admin shell
        // whenever the gateway was unreachable.
        setAuthChecked(true);
        setIsSuperAdmin(false);
        setAuthError(
          err instanceof Error && err.message
            ? err.message
            : 'Could not verify your admin access. Please try again.'
        );
      });
  }, [navigate]);

  // Load tab data
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (tab === 'dashboard') { loadStats(); loadOverview(); }
    else if (tab === 'pending') loadPending();
    else if (tab === 'agents') loadAgents();
    else if (tab === 'super-admins') loadSuperAdmins();
    else if (tab === 'orders') loadOrders();
    else if (tab === 'customers') loadCustomers();
    // agentStatusFilter is a dep so that changing the Agents status dropdown
    // actually refetches — previously it only updated the select's own value.
  }, [tab, isSuperAdmin, agentStatusFilter]);

  async function loadStats() {
    try {
      const data = await adminFetch('/dashboard');
      setStats(data);
    } catch { /* handled at auth level */ }
  }

  async function loadOverview() {
    setOverviewError('');
    try {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [ordersRaw, customersRaw, productsRaw] = await Promise.all([
        shopAdminFetch<WooOrder[] | { data?: WooOrder[] }>('/wc/orders?per_page=50&orderby=date&order=desc'),
        shopAdminFetch<{ id: number; date_created?: string }[] | { data?: { id: number; date_created?: string }[] }>('/wc/customers?per_page=50&orderby=registered_date&order=desc'),
        shopAdminFetch<WooProductStock[] | { data?: WooProductStock[] }>('/wc/products?per_page=100&stock_status=outofstock,onbackorder'),
      ]);

      const allOrders = Array.isArray(ordersRaw) ? ordersRaw : ordersRaw?.data || [];
      const customersList = Array.isArray(customersRaw) ? customersRaw : customersRaw?.data || [];
      const lowStockProducts = Array.isArray(productsRaw) ? productsRaw : productsRaw?.data || [];

      const recentOrders = allOrders.slice(0, 30);
      const ordersLast30d = recentOrders.filter(o => o.date_created && o.date_created >= since);
      const revenue30d = ordersLast30d
        .filter(o => o.status === 'completed' || o.status === 'processing')
        .reduce((sum, o) => sum + (parseFloat(o.total || '0') || 0), 0);
      const newCustomers30d = customersList.filter(c => c.date_created && c.date_created >= since).length;

      setOverview({
        revenue30d,
        orders30d: ordersLast30d.length,
        newCustomers30d,
        recentOrders: recentOrders.slice(0, 4),
        lowStockCount: lowStockProducts.length,
      });
    } catch (err: any) {
      setOverviewError(err?.message || 'Shop data unavailable — check WooCommerce configuration.');
    }
  }

  async function loadPending() {
    setLoading(true);
    try {
      const data = await adminFetch('/agents/pending');
      setPending(data.applications || []);
    } finally {
      setLoading(false);
    }
  }

  async function loadAgents() {
    setLoading(true);
    try {
      const qs = agentStatusFilter ? `?status=${agentStatusFilter}` : '';
      const data = await adminFetch(`/agents/all${qs}`);
      setAgents(data.applications || []);
    } finally {
      setLoading(false);
    }
  }

  async function loadSuperAdmins() {
    setLoading(true);
    try {
      const data = await adminFetch('/super-admins');
      setSuperAdmins(data.users || []);
    } finally {
      setLoading(false);
    }
  }

  async function loadOrders() {
    setLoading(true);
    setOrdersError('');
    try {
      const data = await shopAdminFetch<WooOrder[] | { data?: WooOrder[] }>('/wc/orders?per_page=30');
      const list = Array.isArray(data) ? data : data?.data;
      setOrders(Array.isArray(list) ? list : []);
    } catch (err: unknown) {
      const e = err as Error;
      setOrdersError(e.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }

  async function loadCustomers() {
    setLoading(true);
    setCustomersError('');
    try {
      // Guarded: requires the /wc/customers path to be allow-listed on the gateway
      // (gateway/app/services/woocommerce_rest_client.py — is_allowed_wc_path).
      const data = await shopAdminFetch<WooCustomer[] | { data?: WooCustomer[] }>('/wc/customers?per_page=50');
      const list = Array.isArray(data) ? data : data?.data;
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err: unknown) {
      const e = err as Error;
      setCustomersError(e.message || 'Customer directory requires a backend WooCommerce customers endpoint — not yet enabled.');
    } finally {
      setLoading(false);
    }
  }

  function saveSettings() {
    setSettingsMessage("Settings persistence isn't wired to a backend endpoint yet — nothing was saved.");
  }

  async function approveSuperAdmin(userId: string) {
    setActionBusy(userId);
    try {
      await adminFetch(`/super-admins/${userId}/approve`, { method: 'POST' });
      await loadSuperAdmins();
    } finally {
      setActionBusy(null);
    }
  }

  async function revokeSuperAdmin(userId: string) {
    if (!confirm('Revoke super admin access for this user?')) return;
    setActionBusy(userId);
    try {
      await adminFetch(`/super-admins/${userId}/revoke`, { method: 'POST' });
      await loadSuperAdmins();
    } finally {
      setActionBusy(null);
    }
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#F7F5EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BDD630]" />
      </div>
    );
  }

  // Access could not be confirmed — render nothing privileged.
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-[#F7F5EE] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl font-bold mb-3 text-[#1A2E12]">Access unavailable</h1>
          <p className="mb-6 text-[#6B7B6E]">
            {authError || 'You do not have permission to view the admin portal.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-full font-semibold"
            style={{ background: '#BDD630', color: '#567D38' }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const NAV_GROUPS: { group: string; items: { id: Tab; label: string; icon: React.ElementType; badge?: number }[] }[] = [
    {
      group: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      ],
    },
    {
      group: 'Shop',
      items: [
        { id: 'orders', label: 'Orders', icon: ListOrdered },
      ],
    },
    {
      group: 'Content',
      items: [
        { id: 'content', label: 'Pages & Journal', icon: FileText },
      ],
    },
    {
      group: 'People',
      items: [
        { id: 'customers', label: 'Customers', icon: UserCircle },
        { id: 'pending', label: 'Agent Applications', icon: Clock, badge: stats?.pending_agent_applications },
        { id: 'agents', label: 'Agents (MLM)', icon: Users },
        { id: 'super-admins', label: 'Super Admins', icon: ShieldCheck, badge: stats?.pending_super_admin_approvals || undefined },
      ],
    },
    {
      group: 'System',
      items: [
        { id: 'settings', label: 'Settings', icon: SettingsIcon },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F5EE] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#567D38] text-white flex flex-col shrink-0">
        <div className="px-6 py-5 border-b border-white/10">
          <p className="font-display text-xl font-bold tracking-wide text-[#BDD630]">Marple Admin</p>
          <p className="text-xs text-white/60 mt-0.5">Site Administration</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          <button
            type="button"
            onClick={() => navigate('/admin/shop')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#BDD630] hover:bg-white/10 transition-colors"
          >
            <ShoppingBag className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">Shop back office</span>
          </button>

          {NAV_GROUPS.map(group => (
            <div key={group.group}>
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/50">
                {group.group}
              </p>
              <div className="space-y-1">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const active = tab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        active ? 'bg-[#BDD630] text-[#1A2E12]' : 'text-white/80 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.badge != null && item.badge > 0 && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${active ? 'bg-[#1A2E12] text-[#BDD630]' : 'bg-[#BDD630] text-[#1A2E12]'}`}>
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={() => navigate('/member/dashboard')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Back to Portal
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="px-8 py-8">

          {/* DASHBOARD */}
          {tab === 'dashboard' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-6">Dashboard</h1>

              {overview ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    icon={ShoppingBag}
                    label="Revenue (30d)"
                    value={`R${overview.revenue30d.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`}
                    color="bg-[#BDD630]/20 text-[#567D38]"
                  />
                  <StatCard icon={ListOrdered} label="Orders (30d)" value={overview.orders30d} color="bg-[#BDD630]/20 text-[#567D38]" />
                  <StatCard icon={Users} label="New customers" value={overview.newCustomers30d} color="bg-blue-50 text-blue-600" />
                  <StatCard
                    icon={UserCheck}
                    label="Active agents"
                    value={stats ? stats.total_approved_agents : '—'}
                    color="bg-purple-50 text-purple-600"
                  />
                </div>
              ) : overviewError ? (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {overviewError}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 h-[92px] flex items-center gap-2 text-[#5E6B52]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 mt-6">
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-6">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-[#5E6B52] mb-4">Recent orders</h2>
                  {overview && overview.recentOrders.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[#5E6B52] text-xs uppercase tracking-wide border-b border-[#D9B38C]/20">
                          <th className="pb-2 font-medium">Order</th>
                          <th className="pb-2 font-medium">Customer</th>
                          <th className="pb-2 font-medium">Total</th>
                          <th className="pb-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.recentOrders.map(o => (
                          <tr key={o.id} className="border-b border-[#D9B38C]/10 last:border-0">
                            <td className="py-2.5 text-[#1A2E12]">#{o.id}</td>
                            <td className="py-2.5 text-[#1A2E12]">
                              {`${o.billing?.first_name || ''} ${o.billing?.last_name || ''}`.trim() || '—'}
                            </td>
                            <td className="py-2.5 text-[#1A2E12]">{o.currency || 'R'}{o.total || '—'}</td>
                            <td className="py-2.5">
                              <span className="capitalize px-2.5 py-1 rounded-full text-xs font-medium bg-[#BDD630]/15 text-[#567D38]">
                                {o.status || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-[#5E6B52]">{overviewError ? 'Unavailable.' : 'No recent orders.'}</p>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-6">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-[#5E6B52] mb-4">Needs attention</h2>
                  <div className="space-y-3">
                    {overview && overview.lowStockCount > 0 && (
                      <div className="flex items-start gap-2.5 bg-amber-50 rounded-xl p-3 text-sm text-[#1A2E12]">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        {overview.lowStockCount} product{overview.lowStockCount !== 1 ? 's' : ''} low or out of stock
                      </div>
                    )}
                    {stats && stats.pending_agent_applications > 0 && (
                      <button
                        onClick={() => setTab('pending')}
                        className="w-full text-left flex items-start gap-2.5 bg-[#BDD630]/15 rounded-xl p-3 text-sm text-[#1A2E12] hover:bg-[#BDD630]/25 transition-colors"
                      >
                        <UserCheck className="w-4 h-4 text-[#567D38] shrink-0 mt-0.5" />
                        {stats.pending_agent_applications} agent KYC submission{stats.pending_agent_applications !== 1 ? 's' : ''} pending review
                      </button>
                    )}
                    {stats && stats.pending_super_admin_approvals > 0 && (
                      <button
                        onClick={() => setTab('super-admins')}
                        className="w-full text-left flex items-start gap-2.5 bg-purple-50 rounded-xl p-3 text-sm text-[#1A2E12] hover:bg-purple-100 transition-colors"
                      >
                        <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                        {stats.pending_super_admin_approvals} super admin approval{stats.pending_super_admin_approvals !== 1 ? 's' : ''} pending
                      </button>
                    )}
                    {overview && overview.lowStockCount === 0 &&
                      (!stats || (stats.pending_agent_applications === 0 && stats.pending_super_admin_approvals === 0)) && (
                        <p className="text-sm text-[#5E6B52]">All clear — nothing needs attention.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ORDERS */}
          {tab === 'orders' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">Orders</h1>
              <p className="text-sm text-[#5E6B52] mb-6">
                Live order data from WooCommerce (server-side proxy).
              </p>

              <div className="flex flex-wrap gap-2 mb-5">
                {ORDER_STATUS_FILTERS.map(status => (
                  <button
                    key={status}
                    onClick={() => setOrderStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      orderStatusFilter === status
                        ? 'bg-[#BDD630] text-[#1A2E12] border-[#BDD630]'
                        : 'bg-white text-[#5E6B52] border-[#D9B38C]/40 hover:border-[#BDD630]'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              {ordersError && (
                <div className="mb-4 flex gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {ordersError}
                </div>
              )}

              {loading ? (
                <div className="flex items-center gap-2 text-[#5E6B52]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F5EE]">
                      <tr>
                        {['ID', 'Status', 'Total', 'Date'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5E6B52] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9B38C]/10">
                      {orders
                        .filter(o => orderStatusFilter === 'All' || (o.status || '').toLowerCase() === orderStatusFilter.toLowerCase())
                        .map(o => (
                          <tr key={o.id} className="hover:bg-[#F7F5EE]/50">
                            <td className="px-4 py-3 text-[#5E6B52]">#{o.id}</td>
                            <td className="px-4 py-3 capitalize text-[#1A2E12]">{o.status || '—'}</td>
                            <td className="px-4 py-3 text-[#1A2E12]">{o.total ?? '—'} {o.currency || ''}</td>
                            <td className="px-4 py-3 text-[#5E6B52]">
                              {o.date_created ? new Date(o.date_created).toLocaleString('en-ZA') : '—'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {orders.length === 0 && !loading && (
                    <p className="px-4 py-8 text-center text-[#5E6B52]">No orders returned.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PENDING APPLICATIONS */}
          {tab === 'pending' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-6">Pending Agent Applications</h1>
              {loading ? (
                <div className="flex items-center gap-2 text-[#5E6B52]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : pending.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-12 text-center">
                  <CheckCircle className="w-10 h-10 text-[#BDD630] mx-auto mb-3" />
                  <p className="text-[#1A2E12] font-medium">All clear — no pending applications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pending.map(app => (
                    <div key={app.application_id} className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-[#1A2E12]">{app.first_name} {app.surname}</p>
                          <p className="text-sm text-[#5E6B52]">{app.email}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#5E6B52]">
                            {app.phone && <span>📞 {app.phone}</span>}
                            {app.region && <span>📍 {app.region}</span>}
                            <span>👤 Sponsor: {app.sponsor_name || 'Marple Root'}</span>
                            <span>🗓 {new Date(app.applied_at).toLocaleDateString('en-ZA')}</span>
                          </div>
                          {app.motivation && (
                            <p className="text-xs text-[#5E6B52] mt-2 line-clamp-2 max-w-xl">{app.motivation}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setReviewTarget(app)}
                          className="shrink-0 flex items-center gap-1.5 text-sm font-medium text-[#567D38] hover:text-[#1A2E12] transition-colors"
                        >
                          <Eye className="w-4 h-4" /> Review
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ALL AGENTS */}
          {tab === 'agents' && (
            <div>
              <div className="flex items-center justify-between mb-6 gap-4">
                <h1 className="text-2xl font-semibold text-[#1A2E12]">All Agents</h1>
                <div className="relative">
                  <select
                    value={agentStatusFilter}
                    onChange={e => { setAgentStatusFilter(e.target.value); }}
                    className="appearance-none border border-[#D9B38C]/40 rounded-xl pl-4 pr-8 py-2 text-sm bg-white focus:outline-none focus:border-[#BDD630]"
                  >
                    <option value="">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-[#5E6B52] pointer-events-none" />
                </div>
              </div>

              {loading ? (
                <div className="flex items-center gap-2 text-[#5E6B52]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : agents.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-12 text-center">
                  <Users className="w-10 h-10 text-[#D9B38C] mx-auto mb-3" />
                  <p className="text-[#5E6B52]">No agents found for this filter.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F5EE]">
                      <tr>
                        {['Name', 'Email', 'Code', 'Status', 'Province', 'Sponsor', 'Approved'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5E6B52] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9B38C]/10">
                      {agents.map(agent => (
                        <tr key={agent.user_id} className="hover:bg-[#F7F5EE]/50">
                          <td className="px-4 py-3 font-medium text-[#1A2E12]">{agent.first_name} {agent.surname}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{agent.email}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#567D38]">{agent.agent_code || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                              agent.agent_status === 'approved'
                                ? 'bg-[#BDD630]/20 text-[#567D38]'
                                : agent.agent_status === 'pending'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-50 text-red-600'
                            }`}>
                              {agent.agent_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#5E6B52]">{agent.region || '—'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{agent.sponsor_name || 'Root'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">
                            {agent.approved_at ? new Date(agent.approved_at).toLocaleDateString('en-ZA') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* PAGES & JOURNAL */}
          {tab === 'content' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">Pages &amp; Journal</h1>
              <p className="text-sm text-[#5E6B52] mb-6">
                Content management isn't wired to a CMS yet — this is a placeholder view of site content.
              </p>

              <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F5EE]">
                    <tr>
                      {['ID', 'Title', 'Type', 'Status', 'Last Updated'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5E6B52] uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D9B38C]/10">
                    {PLACEHOLDER_CONTENT.map(item => (
                      <tr key={item.id} className="hover:bg-[#F7F5EE]/50">
                        <td className="px-4 py-3 font-mono text-xs text-[#5E6B52]">{item.id}</td>
                        <td className="px-4 py-3 font-medium text-[#1A2E12]">{item.title}</td>
                        <td className="px-4 py-3 text-[#5E6B52]">{item.type}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            item.status === 'Published' ? 'bg-[#BDD630]/20 text-[#567D38]' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#5E6B52]">
                          {new Date(item.updated).toLocaleDateString('en-ZA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CUSTOMERS */}
          {tab === 'customers' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">Customers</h1>
              <p className="text-sm text-[#5E6B52] mb-6">
                Customer directory sourced from WooCommerce.
              </p>

              {customersError ? (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-12 text-center">
                  <AlertCircle className="w-10 h-10 text-[#D9B38C] mx-auto mb-3" />
                  <p className="text-[#1A2E12] font-medium mb-1">Customer directory unavailable</p>
                  <p className="text-sm text-[#5E6B52]">{customersError}</p>
                </div>
              ) : loading ? (
                <div className="flex items-center gap-2 text-[#5E6B52]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F5EE]">
                      <tr>
                        {['Name', 'Email', 'Orders', 'Status'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5E6B52] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9B38C]/10">
                      {customers.map(c => (
                        <tr key={c.id} className="hover:bg-[#F7F5EE]/50">
                          <td className="px-4 py-3 font-medium text-[#1A2E12]">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{c.email || '—'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{c.orders_count ?? '—'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{c.is_paying_customer ? 'Paying customer' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {customers.length === 0 && !loading && (
                    <p className="px-4 py-8 text-center text-[#5E6B52]">No customers returned.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SETTINGS */}
          {tab === 'settings' && (
            <div className="max-w-xl">
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">Settings</h1>
              <p className="text-sm text-[#5E6B52] mb-6">
                Store-wide configuration. Not yet connected to a backend endpoint.
              </p>

              <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 space-y-4">
                <div>
                  <label className="block text-sm text-[#5E6B52] mb-1">Store name</label>
                  <input
                    value={settingsStoreName}
                    onChange={e => setSettingsStoreName(e.target.value)}
                    className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#BDD630]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#5E6B52] mb-1">Support email</label>
                  <input
                    type="email"
                    value={settingsSupportEmail}
                    onChange={e => setSettingsSupportEmail(e.target.value)}
                    className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#BDD630]"
                  />
                </div>
                <div className="flex items-center justify-between pt-1">
                  <div>
                    <p className="text-sm font-medium text-[#1A2E12]">Maintenance mode</p>
                    <p className="text-xs text-[#5E6B52]">Show a maintenance banner to shoppers</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSettingsMaintenanceMode(v => !v)}
                    className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${
                      settingsMaintenanceMode ? 'bg-[#BDD630]' : 'bg-[#D9B38C]/40'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        settingsMaintenanceMode ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {settingsMessage && (
                  <div className="flex gap-2 text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {settingsMessage}
                  </div>
                )}

                <button
                  onClick={saveSettings}
                  className="bg-[#BDD630] text-[#1A2E12] font-semibold px-5 py-2.5 rounded-xl hover:bg-[#a8be2a] transition-colors"
                >
                  Save settings
                </button>
              </div>
            </div>
          )}

          {/* SUPER ADMINS */}
          {tab === 'super-admins' && (
            <div>
              <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">Super Admins</h1>
              <p className="text-sm text-[#5E6B52] mb-6">
                All @moralo.co.za accounts. Only martin@moralo.co.za and judy@moralo.co.za can approve or revoke access.
              </p>

              {loading ? (
                <div className="flex items-center gap-2 text-[#5E6B52]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F5EE]">
                      <tr>
                        {['Name', 'Email', 'Status', 'Approved By', 'Approved At', 'Joined', 'Actions'].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#5E6B52] uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9B38C]/10">
                      {superAdmins.map(sa => (
                        <tr key={sa.user_id} className="hover:bg-[#F7F5EE]/50">
                          <td className="px-4 py-3 font-medium text-[#1A2E12]">{sa.first_name} {sa.surname}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">{sa.email}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                              sa.is_approved ? 'bg-[#BDD630]/20 text-[#567D38]' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {sa.is_approved ? '✓ Approved' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#5E6B52]">{sa.approved_by_email || '—'}</td>
                          <td className="px-4 py-3 text-[#5E6B52]">
                            {sa.approved_at ? new Date(sa.approved_at).toLocaleDateString('en-ZA') : '—'}
                          </td>
                          <td className="px-4 py-3 text-[#5E6B52]">
                            {new Date(sa.created_at).toLocaleDateString('en-ZA')}
                          </td>
                          <td className="px-4 py-3">
                            {!sa.is_approved ? (
                              <button
                                onClick={() => approveSuperAdmin(sa.user_id)}
                                disabled={actionBusy === sa.user_id}
                                className="text-xs font-medium text-[#567D38] hover:text-[#1A2E12] disabled:opacity-50 flex items-center gap-1"
                              >
                                {actionBusy === sa.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                Approve
                              </button>
                            ) : (
                              <button
                                onClick={() => revokeSuperAdmin(sa.user_id)}
                                disabled={actionBusy === sa.user_id}
                                className="text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-50 flex items-center gap-1"
                              >
                                {actionBusy === sa.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Review modal */}
      {reviewTarget && (
        <ReviewModal
          application={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onDone={async () => {
            setReviewTarget(null);
            await loadPending();
            await loadStats();
          }}
        />
      )}
    </div>
  );
}
